import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Camera, RotateCw, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';

import {
  getStudyDocument,
  STUDY_DOCUMENTS_QUERY_KEY,
  uploadStudyDocument,
  type StudyDocument,
} from './studyApi';
import {
  quoteStudyAnalysis,
  STUDY_ANALYSIS_MAX_PAGES,
  type StudyAnalysis,
} from './studyAnalysisApi';
import { analysisErrorMessage, errorCode } from './studyAnalysisErrors';
import { loadPageImage } from './studyAnalysisPageImages';
import { StudyAnalyseConfirm, type ConfirmPage } from './StudyAnalyseConfirm';
import { StudyExecutionStatus } from './StudyExecutionStatus.tsx';
import {
  defaultPdfPages,
  draftFromPdfPage,
  draftFromPhoto,
  isPdfFile,
  previewUrlFor,
  readyPages,
  revokePreview,
  rotateDraft,
  togglePdfPage,
  TOO_MANY_PHOTOS,
  uploadDraftPages,
  type DraftPage,
} from './studyCaptureDraft';
import { nextRotation } from './studyPageGeometry';
import { openPdf } from './studyPageImage';
import { captureSelectionError, isStudyPresentation } from './studyUploadTypes';
import { StudyPresentationCapture } from './StudyPresentationCapture';
import {
  isSupportedStudyFile,
  STUDY_PHOTO_ACCEPT,
  STUDY_UNSUPPORTED_FILE_MESSAGE,
  StudyUnsupportedFileError,
} from './studyUploadTypes';

/**
 * Capture flow (study-capture-analyse-prd.md §2, §3.2): prepare 1–10 pages (rotate, remove, pick
 * PDF pages), upload them, then the confirm step with the price. Nothing is analysed and no Star
 * is charged until the learner presses Analyse. Saving stays free and separate from analysis:
 * when no page can be prepared here (an unreadable PDF, a PowerPoint), "Just save it" uploads the
 * original file without any page image.
 */

export type CaptureSource =
  | { kind: 'files'; files: File[]; subjectKey?: string }
  | { kind: 'document'; document: StudyDocument };

interface OpenPdf {
  proxy: PDFDocumentProxy;
  file: File | null;
  documentId: string | null;
}

type Step = 'loading' | 'pick' | 'review' | 'uploading' | 'confirm' | 'failed';

const STEP_TITLES: Record<Step, string> = {
  loading: 'Getting your pages ready',
  pick: 'Choose pages to analyse',
  review: 'Check your pages',
  uploading: 'Saving your pages',
  confirm: 'Ready to analyse',
  failed: 'Something went wrong',
};

export function StudyCaptureFlow({
  source,
  ...callbacks
}: {
  source: CaptureSource;
  onClose: () => void;
  onSaved: (fileName: string) => void;
  onAnalysed: (analysis: StudyAnalysis) => void;
}) {
  const selectionError = source.kind === 'files' ? captureSelectionError(source.files) : null;
  if (selectionError)
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Choose your material"
        className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      >
        <section className="w-full max-w-md rounded-2xl bg-canvas-pure p-6 shadow-lg">
          <h2 className="text-xl font-extrabold">Choose your material</h2>
          <p role="alert" className="my-4 text-sm text-slate2">
            {selectionError}
          </p>
          <button type="button" className="btn-pill-primary" onClick={callbacks.onClose}>
            Choose again
          </button>
        </section>
      </div>
    );
  const presentation =
    source.kind === 'document'
      ? isStudyPresentation(source.document)
      : source.files.some(isStudyPresentation);
  return presentation ? (
    <StudyPresentationCapture source={source} {...callbacks} />
  ) : (
    <StudyImageCaptureFlow source={source} {...callbacks} />
  );
}

function StudyImageCaptureFlow({
  source,
  onClose,
  onSaved,
  onAnalysed,
}: {
  source: CaptureSource;
  onClose: () => void;
  /** "Just save it": the material is kept and nothing is analysed. */
  onSaved: (fileName: string) => void;
  onAnalysed: (analysis: StudyAnalysis) => void;
}) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('loading');
  const [pages, setPages] = useState<DraftPage[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [pdfSelection, setPdfSelection] = useState<number[]>([]);
  const [confirmPages, setConfirmPages] = useState<ConfirmPage[]>([]);
  const [saving, setSaving] = useState(false);
  // Originals saved by "Just save it", so Try again never adds a second copy.
  const savedOriginals = useRef(new Set<File>());
  // One holder object for the whole flow, so the unmount cleanup sees the PDF opened later.
  const pdfHolder = useRef<{ value: OpenPdf | null }>({ value: null });
  const cameraInput = useRef<HTMLInputElement>(null);
  const previews = useRef(new Set<string>());
  const fileName =
    source.kind === 'document'
      ? source.document.file_name
      : ((source.files.find(isPdfFile) ?? source.files[0])?.name ?? 'your photos');
  // New photos can be added or retaken only while capturing; an existing material stays as it is.
  const canAddPhotos = source.kind === 'files' && !source.files.some(isPdfFile);

  const track = (list: DraftPage[]) => {
    list.forEach((page) => page.previewUrl && previews.current.add(page.previewUrl));
    return list;
  };
  useEffect(() => {
    const urls = previews.current;
    const holder = pdfHolder.current;
    return () => {
      urls.forEach((url) => revokePreview(url));
      void holder.value?.proxy.destroy();
    };
  }, []);

  async function renderPdfSelection(selection: number[]) {
    const pdf = pdfHolder.current.value;
    if (!pdf) return;
    setStep('loading');
    const drafts: DraftPage[] = [];
    for (const pageNumber of selection) {
      drafts.push(await draftFromPdfPage(pdf.proxy, pageNumber, fileName, pdf.documentId));
    }
    setPages(track(drafts));
    setStep('review');
  }

  async function startPdf(input: { file: File | null; documentId: string | null; url?: string }) {
    const proxy = await openPdf(input.file ?? input.url!);
    pdfHolder.current.value = { proxy, file: input.file, documentId: input.documentId };
    setPdfPageCount(proxy.numPages);
    const selection = defaultPdfPages(proxy.numPages);
    setPdfSelection(selection);
    if (proxy.numPages > STUDY_ANALYSIS_MAX_PAGES) setStep('pick');
    else await renderPdfSelection(selection);
  }

  async function startExistingPhoto(document: StudyDocument) {
    // A page that is already uploaded goes straight to the price; its image cannot change.
    try {
      await quoteStudyAnalysis([{ document_id: document.id, page_number: 1 }]);
      const image = await loadPageImage(document.id, 1).catch(() => null);
      const previewUrl = previewUrlFor(image);
      if (previewUrl) previews.current.add(previewUrl);
      setConfirmPages([{ document_id: document.id, page_number: 1, previewUrl }]);
      setStep('confirm');
      return;
    } catch (error) {
      if (errorCode(error) !== 'STUDY_ANALYSIS_PAGE_NOT_UPLOADED') {
        // Not available, paused or no Stars: the confirm step explains it.
        setConfirmPages([{ document_id: document.id, page_number: 1 }]);
        setStep('confirm');
        return;
      }
    }
    const detail = await getStudyDocument(document.id);
    const response = await fetch(detail.read_url);
    if (!response.ok) throw new Error('This photo could not be opened. Try again.');
    const draft = await draftFromPhoto(await response.blob(), document.file_name, 0, document.id);
    setPages(track([draft]));
    setStep('review');
  }

  // The flow prepares the source it was opened with exactly once (also under StrictMode).
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const run = async () => {
      if (source.kind === 'document') {
        const document = source.document;
        if (document.mime_type === 'application/pdf') {
          const detail = await getStudyDocument(document.id);
          await startPdf({ file: null, documentId: document.id, url: detail.read_url });
        } else {
          await startExistingPhoto(document);
        }
        return;
      }
      // A type the server refuses never reaches page preparation or the upload.
      const supported = source.files.filter(isSupportedStudyFile);
      if (supported.length === 0) throw new StudyUnsupportedFileError();
      if (supported.length < source.files.length) setNote(STUDY_UNSUPPORTED_FILE_MESSAGE);
      const pdf = supported.find(isPdfFile);
      if (pdf) {
        if (supported.length > 1) setNote('Only the PDF is used. Add photos separately.');
        await startPdf({ file: pdf, documentId: null });
        return;
      }
      const photos = supported.slice(0, STUDY_ANALYSIS_MAX_PAGES);
      if (supported.length > photos.length) setNote(TOO_MANY_PHOTOS);
      const drafts: DraftPage[] = [];
      for (const photo of photos) drafts.push(await draftFromPhoto(photo, photo.name));
      setPages(track(drafts));
      setStep('review');
    };
    run().catch((error: unknown) => {
      setFailure(error instanceof Error ? error.message : 'Your pages could not be prepared.');
      setStep('failed');
    });
    // `source` is fixed for the life of the flow; the parent opens a new flow for a new source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function addPhotos(files: FileList | null) {
    const room = STUDY_ANALYSIS_MAX_PAGES - pages.length;
    const all = Array.from(files ?? []);
    const supported = all.filter(isSupportedStudyFile);
    const chosen = supported.slice(0, Math.max(0, room));
    if (supported.length < all.length) setNote(STUDY_UNSUPPORTED_FILE_MESSAGE);
    else if (supported.length > chosen.length) setNote(TOO_MANY_PHOTOS);
    const drafts: DraftPage[] = [];
    for (const file of chosen) drafts.push(await draftFromPhoto(file, file.name));
    setPages((current) => [...current, ...track(drafts)]);
  }

  async function rotate(page: DraftPage) {
    const next = await rotateDraft(page, nextRotation(page.rotation));
    track([next]);
    setPages((current) => current.map((item) => (item.id === page.id ? next : item)));
  }

  function remove(page: DraftPage) {
    setPages((current) => current.filter((item) => item.id !== page.id));
  }

  /** "Just save it" without page images: the original files, as the flow would use them. */
  async function saveOriginals() {
    if (source.kind !== 'files') return;
    const supported = source.files.filter(isSupportedStudyFile);
    const pdf = supported.find(isPdfFile);
    const files = pdf ? [pdf] : supported.slice(0, STUDY_ANALYSIS_MAX_PAGES);
    if (files.length === 0) {
      setFailure(STUDY_UNSUPPORTED_FILE_MESSAGE);
      return;
    }
    setSaving(true);
    try {
      for (const file of files) {
        if (savedOriginals.current.has(file)) continue;
        await uploadStudyDocument(file, source.subjectKey);
        savedOriginals.current.add(file);
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: STUDY_DOCUMENTS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: ['study-materials-library'] }),
      ]);
      onSaved(fileName);
    } catch (error) {
      setFailure(analysisErrorMessage(error, 'Your file did not save. Try again.'));
    } finally {
      setSaving(false);
    }
  }

  async function upload() {
    setStep('uploading');
    setFailure(null);
    const pdf = pdfHolder.current.value;
    try {
      const result = await uploadDraftPages({
        pages,
        pdf: pdf ? { file: pdf.file, documentId: pdf.documentId } : null,
        subjectKey: source.kind === 'files' ? source.subjectKey : undefined,
        onProgress: (next, pdfDocumentId) => {
          setPages(next);
          if (pdf) pdf.documentId = pdfDocumentId;
        },
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: STUDY_DOCUMENTS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: ['study-materials-library'] }),
      ]);
      setConfirmPages(
        readyPages(result.pages).map((page) => ({
          document_id: page.kind === 'pdf' ? result.pdfDocumentId! : page.documentId!,
          page_number: page.pageNumber,
          previewUrl: page.previewUrl,
        })),
      );
      setStep('confirm');
    } catch (error) {
      setFailure(analysisErrorMessage(error, 'Your pages did not finish saving. Try again.'));
      setStep('review');
    }
  }

  const ready = readyPages(pages);
  const uploadedCount = pages.filter((page) => page.uploaded).length;
  // No page can be analysed here, but the file can still be kept (PRD §2: saving is free).
  // An unsupported file cannot be saved either, so it never offers "Just save it".
  const canJustSave =
    source.kind === 'files' &&
    source.files.some(isSupportedStudyFile) &&
    (step === 'failed' || (step === 'review' && ready.length === 0));

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-ink/35 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="study-capture-flow-title"
        className="flex max-h-[100dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-3xl border border-hairline bg-canvas-pure shadow-floating-chrome sm:max-h-[92dvh] sm:rounded-3xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-hairline px-5 py-4 sm:px-7">
          <div className="min-w-0">
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
              Capture
            </p>
            <h2 id="study-capture-flow-title" className="mt-1 text-2xl font-extrabold text-ink">
              {STEP_TITLES[step]}
            </h2>
            <p className="mt-1 truncate text-sm font-medium text-slate2">{fileName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={step === 'uploading'}
            aria-label="Close capture"
            className="rounded-full p-2 text-slate2 hover:bg-surface disabled:opacity-40"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-7">
          {note && (
            <p
              role="status"
              className="mb-4 rounded-2xl bg-wash-sunshine px-4 py-3 text-sm font-bold text-ink"
            >
              {note}
            </p>
          )}
          {step === 'loading' && (
            <StudyExecutionStatus
              phase="upload"
              label="Preparing your pages"
              detail="Making page previews. AI has not started."
            />
          )}
          {step === 'failed' && (
            <p
              role="alert"
              className="rounded-2xl bg-wash-coral px-4 py-3 text-sm font-bold text-ink"
            >
              {failure}
            </p>
          )}
          {step === 'pick' && (
            <PdfPagePicker
              pageCount={pdfPageCount}
              selected={pdfSelection}
              onToggle={(pageNumber) =>
                setPdfSelection((current) => togglePdfPage(current, pageNumber))
              }
            />
          )}
          {(step === 'review' || step === 'uploading') && (
            <>
              {failure && (
                <p
                  role="alert"
                  className="mb-4 rounded-2xl bg-wash-coral px-4 py-3 text-sm font-bold text-ink"
                >
                  {failure}
                </p>
              )}
              <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3" aria-label="Pages">
                {pages.map((page, index) => (
                  <DraftPageCard
                    key={page.id}
                    page={page}
                    index={index}
                    busy={step === 'uploading'}
                    canRetake={canAddPhotos}
                    onRotate={() => void rotate(page)}
                    onRemove={() => remove(page)}
                    onRetake={() => {
                      remove(page);
                      cameraInput.current?.click();
                    }}
                  />
                ))}
              </ul>
              {step === 'uploading' && (
                <StudyExecutionStatus
                  phase="upload"
                  detail={`Saving page ${Math.min(uploadedCount + 1, ready.length)} of ${ready.length}. AI has not started.`}
                />
              )}
            </>
          )}
          {step === 'confirm' && (
            <StudyAnalyseConfirm
              pages={confirmPages}
              onAnalysed={onAnalysed}
              onSave={source.kind === 'files' ? () => onSaved(fileName) : undefined}
            />
          )}
        </div>

        {(step === 'review' || step === 'pick' || canJustSave) && (
          <footer className="flex flex-wrap items-center justify-end gap-3 border-t border-hairline px-5 py-4 sm:px-7">
            {canJustSave && (
              <>
                <p className="mr-auto text-sm font-medium text-slate2">
                  You can still save it to Study without analysis.
                </p>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void saveOriginals()}
                  className="btn-pill-secondary disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Just save it'}
                </button>
              </>
            )}
            {step === 'review' && canAddPhotos && pages.length < STUDY_ANALYSIS_MAX_PAGES && (
              <button
                type="button"
                onClick={() => cameraInput.current?.click()}
                className="btn-pill-secondary mr-auto"
              >
                <Camera className="mr-2 inline h-4 w-4" aria-hidden="true" />
                Add a photo
              </button>
            )}
            {step === 'failed' ? null : step === 'pick' ? (
              <button
                type="button"
                disabled={pdfSelection.length === 0}
                onClick={() => void renderPdfSelection(pdfSelection)}
                className="btn-pill-primary disabled:opacity-50"
              >
                Use {pdfSelection.length} {pdfSelection.length === 1 ? 'page' : 'pages'}
              </button>
            ) : (
              <button
                type="button"
                disabled={ready.length === 0}
                onClick={() => void upload()}
                className="btn-pill-primary disabled:opacity-50"
              >
                {failure ? 'Try again' : 'Continue'}
              </button>
            )}
          </footer>
        )}
        <input
          ref={cameraInput}
          type="file"
          accept={STUDY_PHOTO_ACCEPT}
          capture="environment"
          multiple
          aria-label="Add a photo"
          className="sr-only"
          tabIndex={-1}
          onChange={(event) => {
            void addPhotos(event.target.files);
            event.target.value = '';
          }}
        />
      </div>
    </div>
  );
}

function DraftPageCard({
  page,
  index,
  busy,
  canRetake,
  onRotate,
  onRemove,
  onRetake,
}: {
  page: DraftPage;
  index: number;
  busy: boolean;
  canRetake: boolean;
  onRotate: () => void;
  onRemove: () => void;
  onRetake: () => void;
}) {
  const label = page.kind === 'pdf' ? `Page ${page.pageNumber}` : `Photo ${index + 1}`;
  const hint = page.blurry
    ? 'This photo looks blurry. Retake?'
    : page.dark
      ? 'This photo looks dark. Retake?'
      : null;
  return (
    <li className="rounded-2xl border border-hairline bg-canvas p-2">
      <div className="flex aspect-[3/4] items-center justify-center overflow-hidden rounded-xl bg-surface">
        {page.previewUrl ? (
          <img src={page.previewUrl} alt={label} className="h-full w-full object-contain" />
        ) : (
          <AlertTriangle className="h-6 w-6 text-slate2" aria-hidden="true" />
        )}
      </div>
      <p className="mt-2 text-sm font-extrabold text-ink">
        {label}
        {page.uploaded && <span className="ml-2 text-xs font-bold text-brand-mint">Saved</span>}
      </p>
      {page.error && (
        <p role="alert" className="mt-1 text-xs font-bold text-danger-600">
          {page.error}
        </p>
      )}
      {!page.error && hint && (
        <p className="mt-1 text-xs font-bold text-ink-soft">
          {hint}{' '}
          {canRetake && !busy && !page.uploaded && (
            <button type="button" onClick={onRetake} className="text-brand-sky underline">
              Retake
            </button>
          )}
        </p>
      )}
      {!page.uploaded && (
        <div className="mt-2 flex gap-2">
          {page.kind === 'photo' && page.encoded && (
            <button
              type="button"
              disabled={busy}
              onClick={onRotate}
              aria-label={`Rotate ${label}`}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-hairline text-ink hover:bg-surface disabled:opacity-40"
            >
              <RotateCw className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={onRemove}
            aria-label={`Remove ${label}`}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-hairline text-ink hover:bg-surface disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
    </li>
  );
}

function PdfPagePicker({
  pageCount,
  selected,
  onToggle,
}: {
  pageCount: number;
  selected: number[];
  onToggle: (pageNumber: number) => void;
}) {
  return (
    <div>
      <p className="text-sm font-medium text-ink-soft">
        This PDF has {pageCount} pages. Choose up to {STUDY_ANALYSIS_MAX_PAGES} to analyse.{' '}
        <span className="font-bold text-ink">
          {selected.length} of {STUDY_ANALYSIS_MAX_PAGES} chosen
        </span>
      </p>
      <div
        className="mt-4 grid grid-cols-5 gap-2 sm:grid-cols-8"
        role="group"
        aria-label="PDF pages"
      >
        {Array.from({ length: pageCount }, (_, i) => i + 1).map((pageNumber) => {
          const on = selected.includes(pageNumber);
          return (
            <button
              key={pageNumber}
              type="button"
              aria-pressed={on}
              aria-label={`Page ${pageNumber}`}
              disabled={!on && selected.length >= STUDY_ANALYSIS_MAX_PAGES}
              onClick={() => onToggle(pageNumber)}
              className={`h-11 rounded-xl border text-sm font-extrabold disabled:opacity-40 ${on ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-canvas text-slate2'}`}
            >
              {pageNumber}
            </button>
          );
        })}
      </div>
    </div>
  );
}
